import { ConversationDto } from "@coaching-culture/types";
import { format, fromUnixTime } from "date-fns";
import { sortBy, uniqBy } from "lodash";

const formatUnix = (value: number | null) =>
  value == null ? "" : format(fromUnixTime(value), "dd/MM/yyyy HH:mm");

/**
 * Flattens a user's conversations into one CSV row per occurrence.
 *
 * Private comments are only ever visible to their author, and private
 * notes only to the conversation's manager, so the export applies the
 * same rules — mirroring the API's view rules.
 */
export const buildConversationHistoryRows = (
  conversations: ConversationDto[],
  userId: string,
): Record<string, string>[] => {
  const rows = uniqBy(conversations, (x) => x.id).flatMap((convo) => {
    const isManager = convo.manager.id === userId;
    const role = isManager
      ? "Manager"
      : convo.directReport.id === userId
        ? "Direct Report"
        : "Observer";

    return convo.occurences.map((occ) => {
      const cancelledOn = convo.cancelledOn ?? occ.cancelledOn;
      const cancellationReason =
        convo.cancellationReason ?? occ.cancellationReason;

      const comments = occ.comments.filter(
        (x) => !x.isPrivate || x.createdBy.id === userId,
      );

      const row = {
        "Conversation Type": convo.type,
        Purpose: convo.purpose,
        Manager: convo.manager.name,
        "Direct Report": convo.directReport.name,
        "Your Role": role,
        "Scheduled Date": formatUnix(occ.scheduledDate),
        "Duration (mins)": String(occ.duration),
        Location: occ.location ?? "",
        Recurrence: convo.recurrence,
        Status:
          cancelledOn != null
            ? "Cancelled"
            : occ.completedOn != null
              ? "Completed"
              : "Scheduled",
        "Completed On": formatUnix(occ.completedOn),
        "Cancelled On": formatUnix(cancelledOn),
        "Cancellation Reason": cancellationReason?.text ?? "",
        Observers: occ.observers.map((x) => x.name).join("; "),
        Agenda: occ.agenda.map((x) => x.name).join("; "),
        "Action Items": (occ.actionItems ?? [])
          .map(
            (x) => `${x.name}${x.completedOn != null ? " (Completed)" : ""}`,
          )
          .join("; "),
        "Public Notes": occ.publicNotes ?? "",
        "Private Notes": isManager ? (occ.privateNotes ?? "") : "",
        Comments: comments
          .map(
            (x) =>
              `${x.isPrivate ? "[Private] " : ""}${x.createdBy.name} (${formatUnix(
                x.createdOn,
              )}): ${x.body}`,
          )
          .join("\n"),
      };

      return { scheduledDate: occ.scheduledDate, row };
    });
  });

  return sortBy(rows, (x) => x.scheduledDate).map((x) => x.row);
};
